import cn from 'classnames';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { Icon, Modal } from '@/shared/ui';

import { accessModel } from '@/entities/access';
import { ModalID, modalModel } from '@/entities/modal';
import { patientModel } from '@/entities/patient';

import { SharedDoctorRow } from '@/features/sharedDoctorRow';

import styles from './ShowAllSharedWithModal.module.scss';

export const ShowAllSharedWithModal = () => {
  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();
  const dispatch = useAppDispatch();
  const {
    visible,
    data: { patientID },
  } = useAppSelector(modalModel.selectors.selectAllSharedWithModal);

  const invitations = useAppSelector(
    accessModel.sharingSelectors
      .selectAcceptedOrPendingSharedPatientInvitations,
  );
  const { CanCancelOthersSharings } = useAppSelector(
    patientModel.selectors.selectPatientYourPermissions(patientID),
  );

  const openSharePatientFormModal = useCallback(() => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.SharePatient,
        data: { patientID },
      }),
    );
  }, [patientID]);

  const closeModal = () => {
    dispatch(modalModel.actions.closeModal(ModalID.AllSharedWith));
  };

  return (
    <Modal
      data-component="show-all-shared-with-modal"
      className={cn(styles.container)}
      headerClassName={styles.header}
      bodyClassName={styles.content}
      footerClassName={styles.footer}
      title={formatMessage({
        id: 'patientInfo.sharedWith',
        defaultMessage: 'Shared with',
      })}
      isOpen={visible}
      onOk={openSharePatientFormModal}
      onCancel={closeModal}
      okButtonText={formatMessage({
        id: 'patientInfo.okButtonMobile',
        defaultMessage: 'Share patient file',
      })}
      okButtonProps={{
        variant: 'secondary',
      }}
      shouldRenderCancelButton={false}
      hideFooter={!isMobile}
    >
      {invitations.length === 0 && isMobile && (
        <div className={styles.empty}>
          <Icon className={styles.emptyIcon} name="users" size={40} />
          <p className={cn(styles.emptyText, 'p2')}>
            {formatMessage({
              id: 'patientInfo.emptySharedWith',
              defaultMessage:
                'You have not shared patient with any doctors yet',
            })}
          </p>
        </div>
      )}
      {invitations.map((invitation) => (
        <SharedDoctorRow
          key={invitation.ID}
          canCancelSharing={CanCancelOthersSharings}
          sharedPatientInvitation={invitation}
          isMobile={isMobile}
        />
      ))}
    </Modal>
  );
};
